import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

const MAX_VISIBLE = 40;
const POLL_MS = 200;

function fmtPrice(p) {
  if (p == null) return "";
  const abs = Math.abs(p);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return p.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtQty(q) {
  if (q == null) return "";
  const abs = Math.abs(q);
  if (abs >= 1000) return q.toFixed(0);
  if (abs >= 1) return q.toFixed(3);
  return q.toFixed(4);
}
function timeAgo(ts, now) {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 1) return "now";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

export default function TradeTape({ tradesRef, pair, resetKey }) {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState([]);
  const [tick, setTick] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  useEffect(() => setVisible([]), [resetKey]);
  useEffect(() => setMobileOpen(false), [resetKey]);

  useEffect(() => {
    const id = setInterval(() => {
      const src = tradesRef.current || [];
      setVisible(src.slice(0, MAX_VISIBLE));
      setTick((t) => (t + 1) % 1_000_000);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [tradesRef]);

  const now = Date.now();
  const count = visible.length;
  void tick;

  const header = useMemo(
    () => (
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2">
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400">
            Trades
          </span>
          <span
            className="font-display text-sm font-bold tracking-tight text-white"
            data-testid="trade-tape-pair"
          >
            {pair.base}/{pair.quote}
          </span>
        </div>
        <span
          className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-500"
          data-testid="trade-tape-count"
        >
          {count}
        </span>
      </div>
    ),
    [pair.base, pair.quote, count],
  );

  const renderList = (which) => (
    <div
      className="overflow-y-auto flex-1"
      data-testid={`trade-tape-list-${which}`}
      style={{ scrollbarWidth: "thin" }}
    >
      {visible.length === 0 && (
        <div className="p-4 font-mono-tac text-[10px] text-slate-500 text-center uppercase tracking-[0.24em]">
          waiting for trades…
        </div>
      )}
      {visible.map((t) => (
        <TradeRow key={t.id} t={t} now={now} />
      ))}
    </div>
  );

  if (isMobile) {
    // Peek row content (single most-recent trade rendered with the same layout
    // as expanded rows, per spec).
    const first = visible[0];
    return (
      <>
        {/* Scrim behind the drawer when expanded */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-[1px]"
            onClick={() => setMobileOpen(false)}
            aria-hidden
            data-testid="trade-tape-scrim"
          />
        )}
        <aside
          className={`fixed left-0 right-0 bottom-0 z-[60] cb-hud-chip !bg-[#010308]/95 !border-t !border-white/10 flex flex-col transition-[max-height] duration-200 ${mobileOpen ? "max-h-[75vh]" : "max-h-[68px]"}`}
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="trade-tape-mobile"
        >
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex flex-col w-full min-h-[68px] px-3 border-b border-white/10 bg-black/40 select-none"
            data-testid="trade-tape-mobile-toggle"
          >
            {/* drag handle */}
            <span
              className="mx-auto mt-1.5 mb-1 h-1 w-10 rounded-full bg-white/25"
              aria-hidden
            />
            <div className="flex items-center justify-between gap-2 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-400">
                  Trades
                </span>
                <span className="font-display text-sm font-bold tracking-tight text-white">
                  {pair.base}/{pair.quote}
                </span>
              </div>
              {!mobileOpen && first ? (
                <div
                  className={`flex items-center gap-2 font-mono-tac text-[11px] shrink-0 ${
                    first.isBuyerMaker ? "text-[#FF2A2A]" : "text-[#00FFA3]"
                  }`}
                >
                  {first.isBuyerMaker ? (
                    <ArrowDown className="w-3 h-3" />
                  ) : (
                    <ArrowUp className="w-3 h-3" />
                  )}
                  <span className="tabular-nums">{fmtPrice(first.price)}</span>
                  <span className="text-slate-500 tabular-nums">
                    {fmtQty(first.qty)}
                  </span>
                  <span className="text-slate-600 tabular-nums">
                    {timeAgo(first.timestamp, now)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span
                    className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-500"
                    data-testid="trade-tape-count"
                  >
                    {count}
                  </span>
                  {mobileOpen ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              )}
            </div>
          </button>
          {mobileOpen && renderList("mobile")}
        </aside>
      </>
    );
  }

  // Desktop
  return (
    <aside
      className={`flex flex-col fixed right-0 top-4 bottom-4 z-30 cb-hud-chip !bg-[#010308]/85 !border !border-white/10 transition-[width] duration-200 ${desktopCollapsed ? "w-10" : "w-[300px]"}`}
      data-testid="trade-tape-desktop"
      data-collapsed={desktopCollapsed ? "true" : "false"}
    >
      <button
        type="button"
        className="absolute -left-3 top-4 w-6 h-6 flex items-center justify-center bg-[#010308] border border-white/20 hover:border-white/60 text-white/70 hover:text-white transition-colors"
        onClick={() => setDesktopCollapsed((v) => !v)}
        aria-label={desktopCollapsed ? "Expand trades" : "Collapse trades"}
        data-testid="trade-tape-collapse"
      >
        {desktopCollapsed ? <ChevronDown className="w-3 h-3 rotate-90" /> : <ChevronUp className="w-3 h-3 rotate-90" />}
      </button>
      {!desktopCollapsed && (
        <>
          {header}
          {renderList("desktop")}
        </>
      )}
      {desktopCollapsed && (
        <div className="flex-1 flex items-center justify-center">
          <span
            className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-500"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Trades · {count}
          </span>
        </div>
      )}
    </aside>
  );
}

function TradeRow({ t, now }) {
  const isSell = t.isBuyerMaker;
  const color = isSell ? "text-[#FF2A2A]" : "text-[#00FFA3]";
  const bg = t.aggressive
    ? isSell
      ? "bg-[#590909]/40 border-l-2 border-[#FF2A2A]"
      : "bg-[#025939]/40 border-l-2 border-[#00FFA3]"
    : "border-l-2 border-transparent";
  return (
    <div
      className={`px-3 py-1.5 flex items-center gap-2 border-b border-white/[0.04] font-mono-tac text-[11px] ${bg} ${t.aggressive ? "font-bold" : ""} cb-trade-row`}
      data-testid={`trade-row-${t.id}`}
      data-aggressive={t.aggressive ? "true" : "false"}
      data-side={isSell ? "sell" : "buy"}
    >
      {isSell ? (
        <ArrowDown className={`w-3 h-3 ${color} shrink-0`} />
      ) : (
        <ArrowUp className={`w-3 h-3 ${color} shrink-0`} />
      )}
      <span className={`${color} tabular-nums`}>{fmtPrice(t.price)}</span>
      <span className="text-slate-500 tabular-nums ml-auto">{fmtQty(t.qty)}</span>
      <span className="text-slate-600 tabular-nums w-8 text-right">
        {timeAgo(t.timestamp, now)}
      </span>
    </div>
  );
}
